import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Check, 
  X, 
  Eye, 
  Clock, 
  AlertTriangle, 
  Award,
  Calendar,
  User,
  Trash2
} from 'lucide-react';
import { useToast } from '@/hooks/toast';
import { useAuth } from '@/context/AuthContext';
import AdminPremiumDialog from './AdminPremiumDialog';
import ConfirmationDialog from '../user/ConfirmationDialog';
import { getListingPremiumDetails } from '@/lib/actions/adminPremiumActions';
import { LoadingSpinner } from '@/components/ui/loading-spinner';

interface Listing {
  id: number;
  title: string;
  description: string;
  price: number;
  location: string;
  images: string[];
  approval_status: string;
  created_at: string;
  is_premium: boolean;
  profiles?: {
    username: string;
    email: string;
  };
  categories: {
    name: string;
  };
}

interface PremiumDetails {
  isPremium: boolean;
  isAdminGranted: boolean;
  grantedBy?: string;
  adminNotes?: string;
  expiresAt?: Date;
}

const LISTINGS_PER_PAGE = 10;

const ListingApproval = () => {
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [premiumDetails, setPremiumDetails] = useState<Record<number, PremiumDetails>>({});
  const [premiumDialogOpen, setPremiumDialogOpen] = useState(false);
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [listingToDelete, setListingToDelete] = useState<Listing | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { toast } = useToast();
  const { profile } = useAuth();

  const fetchListings = async (page: number = 0, append: boolean = false) => {
    try {
      if (page === 0) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const from = page * LISTINGS_PER_PAGE;
      const to = from + LISTINGS_PER_PAGE - 1;

      let query = supabase
        .from('listings')
        .select(`
          id,
          title,
          description,
          price,
          location,
          images,
          approval_status,
          created_at,
          is_premium,
          user_id,
          categories!listings_category_id_fkey (
            name
          )
        `)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (statusFilter !== 'all') {
        query = query.eq('approval_status', statusFilter);
      }

      const { data: listingsData, error } = await query;

      if (error) throw error;

      // Check if we have more data
      const hasMoreData = (listingsData || []).length === LISTINGS_PER_PAGE;
      setHasMore(hasMoreData);

      // Fetch profile data separately for each listing
      const listingsWithProfiles = await Promise.all(
        (listingsData || []).map(async (listing) => {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('username, email')
            .eq('id', listing.user_id)
            .single();

          return {
            ...listing,
            profiles: profileData || undefined
          };
        })
      );

      if (append) {
        setListings(prev => [...prev, ...listingsWithProfiles]);
      } else {
        setListings(listingsWithProfiles);
      }

      // Fetch premium details for each listing
      const premiumDetailsPromises = listingsWithProfiles.map(async (listing) => {
        const details = await getListingPremiumDetails(listing.id);
        return { listingId: listing.id, details };
      });

      const premiumDetailsResults = await Promise.all(premiumDetailsPromises);
      const premiumDetailsMap = premiumDetailsResults.reduce((acc, { listingId, details }) => {
        if (details) {
          acc[listingId] = details;
        }
        return acc;
      }, {} as Record<number, PremiumDetails>);

      if (append) {
        setPremiumDetails(prev => ({ ...prev, ...premiumDetailsMap }));
      } else {
        setPremiumDetails(premiumDetailsMap);
      }
    } catch (error) {
      console.error('Error fetching listings:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch listings',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreListings = useCallback(() => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchListings(nextPage, true);
    }
  }, [currentPage, loadingMore, hasMore]);

  useEffect(() => {
    setCurrentPage(0);
    setHasMore(true);
    fetchListings(0, false);
  }, [statusFilter]);

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop + 100 >=
        document.documentElement.offsetHeight
      ) {
        loadMoreListings();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadMoreListings]);

  const updateApprovalStatus = async (id: number, status: string, adminNotes?: string) => {
    try {
      const updateData: any = {
        approval_status: status,
        admin_notes: adminNotes,
        reviewed_by: profile?.id,
        reviewed_at: new Date().toISOString()
      };

      // Set is_active based on approval status
      if (status === 'approved') {
        updateData.is_active = true;
        updateData.last_active_at = new Date().toISOString();
      } else if (status === 'rejected') {
        updateData.is_active = false;
      }

      const { error } = await supabase
        .from('listings')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Listing ${status} successfully`,
      });

      // Reset to first page and reload
      setCurrentPage(0);
      setHasMore(true);
      fetchListings(0, false);
    } catch (error) {
      console.error('Error updating approval status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update listing status',
        variant: 'destructive',
      });
    }
  };

  const deleteListing = async (id: number) => {
    setDeleting(true);
    try {
      const { error } = await supabase
        .from('listings')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Listing deleted successfully',
      });

      // Reset to first page and reload
      setCurrentPage(0);
      setHasMore(true);
      fetchListings(0, false);
    } catch (error) {
      console.error('Error deleting listing:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete listing',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setListingToDelete(null);
    }
  };

  const openDeleteDialog = (listing: Listing) => {
    setListingToDelete(listing);
    setDeleteDialogOpen(true);
  };

  const openPremiumDialog = (listing: Listing) => {
    setSelectedListing(listing);
    setPremiumDialogOpen(true);
  };

  const handlePremiumSuccess = () => {
    // Reset to first page and reload
    setCurrentPage(0);
    setHasMore(true);
    fetchListings(0, false);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <Check className="h-4 w-4 text-green-600" />;
      case 'rejected':
        return <X className="h-4 w-4 text-red-600" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'approved':
        return 'default';
      case 'rejected':
        return 'destructive';
      case 'pending':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  if (loading) return <LoadingSpinner className="mt-8" />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Listing Approval</h2>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Listings</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-4">
        {listings.map((listing) => {
          const premium = premiumDetails[listing.id];
          return (
            <Card key={listing.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="space-y-2">
                    <CardTitle className="text-lg">{listing.title}</CardTitle>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span>by {listing.profiles?.username || listing.profiles?.email || 'Unknown User'}</span>
                      <span>•</span>
                      <span>{listing.categories?.name}</span>
                      <span>•</span>
                      <span>{new Date(listing.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant={getStatusBadgeVariant(listing.approval_status)}
                        className="flex items-center gap-1"
                      >
                        {getStatusIcon(listing.approval_status)}
                        {listing.approval_status}
                      </Badge>
                      
                      {listing.is_premium && (
                        <Badge className="bg-amber-500 hover:bg-amber-600 text-white flex items-center gap-1">
                          <Award className="h-3 w-3" />
                          Premium
                          {premium?.isAdminGranted && (
                            <span className="text-xs">(Admin)</span>
                          )}
                        </Badge>
                      )}
                    </div>

                    {premium?.isAdminGranted && (
                      <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded border">
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          <span>Granted by: {premium.grantedBy}</span>
                        </div>
                        {premium.expiresAt && (
                          <div className="flex items-center gap-1 mt-1">
                            <Calendar className="h-3 w-3" />
                            <span>Expires: {premium.expiresAt.toLocaleDateString()}</span>
                          </div>
                        )}
                        {premium.adminNotes && (
                          <div className="mt-1">
                            <span className="font-medium">Notes:</span> {premium.adminNotes}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`/listing/${listing.id}`, '_blank')}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    
                    <Button
                      variant={listing.is_premium ? "destructive" : "default"}
                      size="sm"
                      onClick={() => openPremiumDialog(listing)}
                      className={listing.is_premium ? 
                        "bg-amber-600 hover:bg-amber-700" : 
                        "bg-amber-500 hover:bg-amber-600"
                      }
                    >
                      <Award className="h-4 w-4 mr-1" />
                      {listing.is_premium ? 'Remove Premium' : 'Make Premium'}
                    </Button>

                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => openDeleteDialog(listing)}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                <p className="text-gray-700 mb-4 line-clamp-2">{listing.description}</p>
                <p className="font-semibold text-lg mb-4">€{listing.price}</p>
                
                {listing.approval_status === 'pending' && (
                  <div className="space-y-3">
                    <Textarea
                      placeholder="Admin notes (optional)"
                      id={`notes-${listing.id}`}
                      className="min-h-[60px]"
                    />
                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          const notes = (document.getElementById(`notes-${listing.id}`) as HTMLTextAreaElement)?.value;
                          updateApprovalStatus(listing.id, 'approved', notes);
                        }}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => {
                          const notes = (document.getElementById(`notes-${listing.id}`) as HTMLTextAreaElement)?.value;
                          updateApprovalStatus(listing.id, 'rejected', notes);
                        }}
                        variant="destructive"
                      >
                        <X className="h-4 w-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}

        {loadingMore && (
          <div className="flex justify-center py-4">
            <LoadingSpinner />
          </div>
        )}

        {!hasMore && listings.length > 0 && (
          <div className="text-center py-4 text-gray-500">
            No more listings to load
          </div>
        )}

        {!loading && listings.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No listings found for the selected filter
          </div>
        )}
      </div>

      {selectedListing && (
        <AdminPremiumDialog
          isOpen={premiumDialogOpen}
          onClose={() => setPremiumDialogOpen(false)}
          listingId={selectedListing.id}
          listingTitle={selectedListing.title}
          adminId={profile?.id || ''}
          currentPremiumStatus={selectedListing.is_premium}
          isAdminGranted={premiumDetails[selectedListing.id]?.isAdminGranted}
          onSuccess={handlePremiumSuccess}
        />
      )}

      <ConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={() => listingToDelete && deleteListing(listingToDelete.id)}
        title="Delete Listing"
        description={`Are you sure you want to permanently delete "${listingToDelete?.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isDangerous={true}
        isProcessing={deleting}
      />
    </div>
  );
};

export default ListingApproval;
